import { Popover } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlaceIcon from "@mui/icons-material/Place";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import StarIcon from "@mui/icons-material/Star";
import VerifiedIcon from "@mui/icons-material/Verified";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import base_url from "../../api/bootapi";
import FooterNew from "../NavBar/FooterNew";
import Nav from "../NavBar/Nav";
import { useExperienceCart } from "./ExperienceCartContext";
import "./Experiences.css";
import {
    cacheProduct,
    formatDate,
    formatDisplayDate,
    formatMoney,
    getAllImages,
    getCachedProduct,
    getCoverImage,
    getCurrency,
    getDurationLabel,
    getFromPrice,
    getRating,
    getReviewCount,
    hasFreeCancellation,
} from "./experienceUtils";

export default function ExperienceDetails() {
  const { productCode } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addItem } = useExperienceCart();

  const initialProduct = (location.state && location.state.product) || getCachedProduct(productCode);
  const destination = location.state && location.state.destination;

  const [product, setProduct] = useState(initialProduct);

  
  const [activeImage, setActiveImage] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [travelers, setTravelers] = useState(2);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [productCode]);

  // The search results only ever carry a couple of photos per product —
  // Viator's product-details endpoint returns the full photo set (plus the
  // richer description). Fetch it in the background and merge it in once
  // it arrives so the gallery/thumbnail strip fills out; if the endpoint
  // isn't available for any reason we just silently keep what we already
  // have from the search result, no error shown to the user.
  useEffect(() => {
    if (!productCode) return;
    let cancelled = false;

    axios
      .get(`${base_url.api1}/tourapp/viator/products/${productCode}`)
      .then((res) => {
        if (cancelled) return;
        const payload = res && res.data;
        const fullProduct =
          payload && (payload.viator_response || payload.data || payload);
        if (!fullProduct) return;
        setProduct((prev) => {
          const merged = { ...(prev || {}), ...fullProduct };
          if (!(fullProduct.images && fullProduct.images.length) && prev && prev.images) {
            merged.images = prev.images;
          }
          cacheProduct(merged);
          return merged;
        });
        setActiveImage(0);
      })
      .catch(() => {
        // No richer details available — the cached/search-result product
        // (already shown) remains as-is.
      });

    return () => {
      cancelled = true;
    };
  }, [productCode]);

  const images = useMemo(() => getAllImages(product), [product]);
  const cover = useMemo(() => getCoverImage(product), [product]);
  const gallery = images.length ? images : cover ? [cover] : [];

  if (!product) {
    return (
      <div className="exp-scope">
        <Nav />
        <div className="exp-page-loading">
          <div>
            <p style={{ marginBottom: 16 }}>
              We couldn't find that experience — it may have come from a link that's gone stale.
            </p>
            <button className="exp-inline-btn" onClick={() => navigate("/experiences")}>
              Start a new search
            </button>
          </div>
        </div>
        <FooterNew />
      </div>
    );
  }

  const rating = getRating(product);
  const reviewCount = getReviewCount(product);
  const duration = getDurationLabel(product);
  const price = getFromPrice(product);
  const currency = getCurrency(product);
  const freeCancel = hasFreeCancellation(product);

  const handlePrevImage = () =>
    setActiveImage((i) => (i === 0 ? gallery.length - 1 : i - 1));
  const handleNextImage = () =>
    setActiveImage((i) => (i === gallery.length - 1 ? 0 : i + 1));

  const handleBookNow = () => {
    // addItem(product, {
    //   image: cover,
    //   images: gallery,
    //   price,
    //   currency,
    //   date: selectedDate ? formatDate(selectedDate) : null,
    //   travelers,
    //   destinationName: destination ? destination.name : "",
    // });

   
    
    window.open(`${base_url.api1}/tourapp/viator/products/${productCode}/redirect`, "_blank");

   
   
  };

  return (
    <div className="exp-scope">
      <ToastContainer position="top-center" autoClose={2500} hideProgressBar />
      <div style={{ position: "relative" }}>
        <Nav />
      </div>

      <div className="exp-container">
        <div className="exp-breadcrumb">
          <a onClick={() => navigate("/experiences")}>Experiences</a>
          <span>/</span>
          {destination && (
            <>
              <a
                onClick={() =>
                  navigate(
                    `/experiences/results?destination_id=${destination.destination_id}&name=${encodeURIComponent(
                      destination.name
                    )}`,
                    { state: { destination } }
                  )
                }
              >
                {destination.name}
              </a>
              <span>/</span>
            </>
          )}
          <span className="exp-crumb-current">{product.title}</span>
        </div>

        <div className="exp-details-title-row">
          <h1 className="exp-details-title">{product.title}</h1>
        </div>

        <div className="exp-details-subrow">
          {rating && (
            <span className="exp-details-rating">
              <StarIcon fontSize="small" />
              {rating.toFixed(1)}
              {reviewCount ? ` (${reviewCount.toLocaleString()} reviews)` : ""}
            </span>
          )}
          {duration && (
            <span>
              <AccessTimeIcon fontSize="inherit" style={{ verticalAlign: "-2px" }} /> {duration}
            </span>
          )}
          {destination && (
            <span>
              <PlaceIcon fontSize="inherit" style={{ verticalAlign: "-2px" }} /> {destination.name}
            </span>
          )}
          <span className="exp-price-guarantee-pill">
            <VerifiedIcon fontSize="inherit" /> Best Price Guarantee
          </span>
        </div>

        <div className={`exp-gallery${gallery.length > 1 ? "" : " exp-gallery-single"}`}>
          {gallery.length > 1 && (
            <div className="exp-gallery-thumbs">
              {gallery.map((src, idx) => (
                <div
                  key={src}
                  className={`exp-gallery-thumb ${idx === activeImage ? "active" : ""}`}
                  onClick={() => setActiveImage(idx)}
                >
                  <img src={src} alt={`${product.title} ${idx + 1}`} />
                </div>
              ))}
            </div>
          )}
          <div className="exp-gallery-main">
            {gallery.length > 0 ? (
              <img src={gallery[activeImage]} alt={product.title} />
            ) : (
              <div className="exp-product-image-placeholder" style={{ position: "absolute", inset: 0 }} />
            )}
            {gallery.length > 1 && (
              <>
                <button className="exp-gallery-nav prev" onClick={handlePrevImage} aria-label="Previous image">
                  <ChevronLeftIcon />
                </button>
                <button className="exp-gallery-nav next" onClick={handleNextImage} aria-label="Next image">
                  <ChevronRightIcon />
                </button>
                <div className="exp-gallery-count">
                  {activeImage + 1} / {gallery.length}
                </div>
              </>
            )}
            <div className="exp-gallery-overlay-actions">
              <button
                type="button"
                className="exp-gallery-share"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: product.title, url: window.location.href }).catch(() => {});
                  } else if (navigator.clipboard) {
                    navigator.clipboard.writeText(window.location.href);
                    toast.info("Link copied");
                  }
                }}
              >
                <ShareOutlinedIcon fontSize="small" /> Share
              </button>
            </div>
          </div>
        </div>

        <div className="exp-details-body">
          <div>
            {product.flags && product.flags.length > 0 && (
              <div className="exp-flag-list">
                {product.flags.includes("FREE_CANCELLATION") && (
                  <span className="exp-flag-pill">Free cancellation</span>
                )}
                {product.flags.includes("SKIP_THE_LINE") && (
                  <span className="exp-flag-pill">Skip the line</span>
                )}
                {product.confirmationType === "INSTANT" && (
                  <span className="exp-flag-pill">Instant confirmation</span>
                )}
              </div>
            )}

            <div className="exp-details-section">
              <h2>About this experience</h2>
              <p className="exp-details-description">{product.description}</p>
            </div>
          </div>

          <div className="exp-booking-card">
            <div className="exp-booking-price-wrap">
              <div className="exp-booking-price-label">From</div>
              <div className="exp-booking-price">
                {price !== null ? formatMoney(price, currency) : "--"}
                <span>per person</span>
              </div>
            </div>

            {/* <div className="exp-booking-field-grid">
              <Popover
                opened={showDatePicker}
                onChange={setShowDatePicker}
                position="bottom-start"
                shadow="md"
                withinPortal
              >
                <Popover.Target>
                  <div className="exp-booking-field" onClick={() => setShowDatePicker((o) => !o)}>
                    <label>Date</label>
                    <input
                      type="text"
                      readOnly
                      value={selectedDate ? formatDisplayDate(selectedDate) : ""}
                      placeholder="Select a date"
                    />
                  </div>
                </Popover.Target>
                <Popover.Dropdown>
                  <DatePicker
                    value={selectedDate}
                    onChange={(d) => {
                      setSelectedDate(d);
                      setShowDatePicker(false);
                    }}
                    minDate={new Date()}
                  />
                </Popover.Dropdown>
              </Popover>

              <div className="exp-booking-field" style={{ cursor: "default" }}>
                <label>Travelers</label>
                <div className="exp-traveler-stepper">
                  <span>{travelers}</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      className="exp-stepper-btn"
                      onClick={() => setTravelers((t) => Math.max(1, t - 1))}
                      disabled={travelers <= 1}
                    >
                      −
                    </button>
                    <button
                      className="exp-stepper-btn"
                      onClick={() => setTravelers((t) => Math.min(20, t + 1))}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div> */}

            <button className="exp-btn-primary" onClick={handleBookNow}>
              Book Now
            </button>

            <div className="exp-booking-note">
              {freeCancel ? (
                <>
                  <CheckCircleIcon fontSize="small" />
                  <span>
                    <u>Free cancellation</u> up to 24 hours before the experience starts (local time)
                  </span>
                </>
              ) : (
                <>
                  <InfoOutlinedIcon fontSize="small" />
                  <span>This experience is non-refundable once booked.</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <FooterNew />
    </div>
  );
}
